import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy, Component, DestroyRef, booleanAttribute, computed, effect, inject, input,
  numberAttribute, output, signal, untracked,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { AiAgentService } from '../../../services/API/ai-agent.service';
import { AiLayoutService } from '../../../services/ai-layout.service';
import { OneBadgeComponent } from '../../one-badge/one-badge.component';

/** Duração total da "chegada" (faixa acende 0,9 s, apaga até 2,1 s; minhoca desce 1,1→1,9 s e
 *  contorna a aba até 2,35 s; pulso da aba até ~2,8 s); casa com ai-aba.component.css. */
export const AI_ABA_CHEGADA_MS = 3000;

/** Geometria espelhada do CSS. A faixa e a minhoca começam ABAIXO da barra superior do menu
 *  (`.main-wrapper { padding-top: 60px }`, menu.component.css) e a minhoca contorna a aba
 *  (`.ai-aba`: 36×72, raio 12 nos cantos esquerdos, 40px do chão). */
const TOPO_MENU = 60;
const ABA_LARGURA = 36;
const ABA_ALTURA = 72;
const ABA_RAIO = 12;
const ABA_BOTTOM = 40;
/** Largura da faixa e do SVG da minhoca, colados à borda direita (decisão do usuário: 50px). */
export const FAIXA_LARGURA = 50;
/** Comprimento normalizado do caminho da minhoca (`pathLength` no template). */
const MINHOCA_COMPRIMENTO = 1000;
/** Tamanho do traço da minhoca em unidades normalizadas (casa com `stroke-dasharray` no CSS). */
const MINHOCA_TRACO = 60;
/** Piso de viewport da aba (`@media (max-width: 992px)` no CSS): abaixo disso não há chamada. */
const ABA_PISO_VIEWPORT_PX = 993;

/**
 * Aba-gaveta do assistente (spec §5): presa à borda direita da tela quando fechado; quando o
 * painel está acoplado, ela "gruda" na borda ESQUERDA do painel (mesma cor, sem borda direita,
 * sombra só à esquerda) e o clique passa a fechar em vez de abrir. Some no palco/chat flutuante
 * (aberto sem estar acoplado) e sem usuário autenticado — como o antigo `.ai-launcher`.
 *
 * Review Task 6, fix round 1 (Critical 1): `temUsuario` chega como @Input em vez de injetar
 * `UnifiedAuthService` diretamente. Motivo — bug real de produção, não hipotético: o overlay
 * (`AiAssistantOverlayComponent`) é Default-CD e já expõe `temUsuario()` (lê `sessionStorage` via
 * `UnifiedAuthService`, não reativo); se esta aba lesse o serviço por conta própria dentro de um
 * `computed`, a primeira avaliação — tipicamente na tela de login, deslogado — nunca leria
 * `svc.aberto()`/`layout.acoplado()` (curto-circuito do `&&`), o `computed` nunca registraria
 * essas dependências, e a aba ficaria escondida PARA SEMPRE mesmo depois do login navegar para
 * `/home` (nada dispara um recomputo). Como @Input, é o próprio Angular quem marca este componente
 * OnPush para checagem a cada escrita — e como o overlay é Default-CD, `temUsuario()` é relido a
 * cada ciclo dele, então o valor chega sempre fresco.
 *
 * Chamada (decisão do usuário, 2026-09-17/18): `chamando` = há balão de AJUDA ou de TOUR visível
 * (liga a classe `is-chamando`). A chegada — montada só durante `AI_ABA_CHEGADA_MS` e só em
 * desktop (≥ 993px) — é uma "faixa" de luz estilo Echo Show na borda direita, abaixo da barra do
 * menu, que se acende da direita para a esquerda em degradê até 50px e depois se apaga de cima
 * para baixo até o topo da aba, enquanto uma "minhoca" (traço fino de luz num SVG) desce junto
 * com a frente que se apaga e contorna a aba; ao final a aba dá um pulso leve de escala. Roda a
 * cada MUDANÇA do contador `disparo`: o overlay o incrementa quando uma
 * notificação de ajuda/tour aparece ou é substituída por outra e quando o usuário chega à Home.
 * Descer `chamando` cancela uma chegada em curso. O brilho metálico (só `transform`, compositor)
 * atravessa a aba a cada 5,2 s SEMPRE que ela está visível — em repouso é a única animação viva.
 */
@Component({
  selector: 'ai-aba',
  standalone: true,
  imports: [MatIconModule, OneBadgeComponent],
  templateUrl: './ai-aba.component.html',
  styleUrl: './ai-aba.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAbaComponent {
  readonly svc = inject(AiAgentService);
  readonly layout = inject(AiLayoutService);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  /** Usuário autenticado — escrito pelo overlay a cada ciclo (`[temUsuario]="temUsuario()"`). */
  temUsuario = input(false, { transform: booleanAttribute });

  /** Balão de ajuda ou de tour visível — escrito pelo overlay (`[chamando]="chamandoAba()"`). */
  chamando = input(false, { transform: booleanAttribute });

  /** Contador de chegadas — cada mudança (para > 0) roda a faixa; escrito pelo overlay
   *  (`[disparo]="disparoAba()"`): nova notificação de ajuda/tour ou chegada à Home. */
  disparo = input(0, { transform: numberAttribute });

  abrir = output<void>();

  /** Fechado, OU aberto-e-acoplado (presa à borda do painel); nunca no palco/chat flutuante. */
  readonly visivel = computed(() => this.temUsuario() && (!this.svc.aberto() || this.layout.acoplado()));
  readonly acoplada = computed(() => this.svc.aberto() && this.layout.acoplado());

  /** Minor #8 (review Task 6) — trunca o contador do badge em '9+' acima de 9. */
  readonly naoLidosLabel = computed(() => {
    const n = this.svc.naoLidos();
    return n > 9 ? '9+' : String(n);
  });

  /** Chegada em curso (faixa + minhoca montadas, pulso da aba). */
  readonly chegando = signal(false);
  /** Altura da janela lida UMA vez por chegada — o SVG da minhoca usa px = unidades do viewBox. */
  readonly alturaMinhoca = signal(0);
  readonly caminhoMinhoca = signal('');
  /** `stroke-dashoffset` em que a minhoca termina a DESCIDA (topo da aba) e começa o contorno —
   *  depende da altura da janela, por isso vai como variável CSS para os keyframes. */
  readonly fimDescidaMinhoca = signal(0);
  readonly faixaLargura = FAIXA_LARGURA;

  private timerChegada: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Bordas dos @Inputs: a faixa roda a cada MUDANÇA de `disparo` (o overlay reescreve os inputs
    // a cada ciclo dele, então valor igual não redispara); a DESCIDA de `chamando` cancela o que
    // estiver em curso. Sem dependência entre os dois: a Home dispara com `chamando` falso.
    let chamandoAnterior = false;
    let disparoAnterior = 0;
    effect(() => {
      const chamando = this.chamando();
      const disparo = this.disparo();
      const desceu = !chamando && chamandoAnterior;
      const novoDisparo = disparo !== disparoAnterior && disparo > 0;
      chamandoAnterior = chamando;
      disparoAnterior = disparo;
      untracked(() => {
        if (novoDisparo) this.iniciarChegada();
        else if (desceu) this.cancelarChegada();
      });
    });
    this.destroyRef.onDestroy(() => this.limparTimer());
  }

  clicar(): void {
    if (this.acoplada()) this.svc.fechar(); else this.abrir.emit();
  }

  private iniciarChegada(): void {
    const win = this.doc.defaultView;
    const altura = win?.innerHeight ?? 0;
    // Mobile/tablet (< 993px): a aba nem existe (CSS) — não monta faixa nem minhoca, nem timer.
    if (!win || altura <= 0 || win.innerWidth < ABA_PISO_VIEWPORT_PX) return;
    this.alturaMinhoca.set(altura);
    this.caminhoMinhoca.set(this.desenharCaminho(altura));
    this.fimDescidaMinhoca.set(this.calcularFimDescida(altura));
    this.chegando.set(true);
    this.limparTimer();
    this.timerChegada = setTimeout(() => {
      this.timerChegada = null;
      this.chegando.set(false);
    }, AI_ABA_CHEGADA_MS);
  }

  private cancelarChegada(): void {
    this.limparTimer();
    this.chegando.set(false);
  }

  private limparTimer(): void {
    if (this.timerChegada) clearTimeout(this.timerChegada);
    this.timerChegada = null;
  }

  /**
   * Caminho da minhoca em px (viewBox = FAIXA_LARGURA × altura da janela): nasce logo abaixo da
   * barra do menu, colado à borda direita, desce até o topo da aba, percorre o topo dela para a
   * esquerda, contorna os dois cantos arredondados e volta pela base até a borda.
   * `pathLength="1000"` no template normaliza o comprimento.
   */
  private desenharCaminho(altura: number): string {
    const xBorda = FAIXA_LARGURA;
    const xEsq = xBorda - ABA_LARGURA;
    const yTopo = altura - ABA_BOTTOM - ABA_ALTURA;
    const yBase = altura - ABA_BOTTOM;
    const r = ABA_RAIO;
    return [
      `M ${xBorda - 1} ${TOPO_MENU}`,
      `V ${yTopo}`,
      `H ${xEsq + r}`,
      `A ${r} ${r} 0 0 0 ${xEsq} ${yTopo + r}`,
      `V ${yBase - r}`,
      `A ${r} ${r} 0 0 0 ${xEsq + r} ${yBase}`,
      `H ${xBorda}`,
    ].join(' ');
  }

  /**
   * Quanto do caminho normalizado é a descida (borda do menu → topo da aba). O contorno da aba tem
   * comprimento fixo (topo + 2 arcos + lado + base); a descida varia com a janela. Devolve o
   * `stroke-dashoffset` em que a cabeça do traço chega ao topo da aba: offset = traço − descida.
   */
  private calcularFimDescida(altura: number): number {
    const descidaPx = altura - ABA_BOTTOM - ABA_ALTURA - TOPO_MENU;
    const contornoPx = (ABA_LARGURA - ABA_RAIO) + (Math.PI * ABA_RAIO / 2) * 2
      + (ABA_ALTURA - 2 * ABA_RAIO) + (ABA_LARGURA - ABA_RAIO);
    const descidaNorm = MINHOCA_COMPRIMENTO * descidaPx / (descidaPx + contornoPx);
    return Math.round(MINHOCA_TRACO - descidaNorm);
  }
}
